/**
 * @module rate_gate_actions
 * @redux
 * @description Redux thunks for the public rate gate (email + invisible
 * reCAPTCHA unlock over pf-comms). See notes/public-rate-gate-PRD.md and
 * notes/public-rate-gate-structs-P1.md. The pure HTTP mapping lives in
 * util/rateGateApi.js; these thunks own the store transitions:
 *
 *   idle → verifying → unlocked | gated | error (inline, retryable)
 */
import { SET_RATE_GATE, SET_DISCOUNT, SET_QUERY_PARAMS } from './types';
import * as rateGateApi from '../util/rateGateApi';
import { UnlockOutcome } from '../util/rateGateApi';
import { buildSearchContext } from '../util/searchContext';
import { RateGateStatus, RATE_GATE_ERROR_COPY } from '../util/rateGate';
import { saveSearchBlurCardsEmail } from './cache_actions';
import { showSearchBlurCards } from './code_config_actions';
import { getJSON, saveBookingFormInfo } from './route_actions';
import { buildPublicBookingPayload } from '../util/publicBookingPayload';

const setRateGate = (payload) => ({ type: SET_RATE_GATE, payload });

/**
 * Apply an unlock DiscountGrant the same way the existing `?discount=` URL
 * path does: the grant object lands in `state.route.discount` (SET_DISCOUNT —
 * card price-source + booking payloads read `.id`), the CODE lands in
 * `queryParams.discount` (the search request reads it as `discountCode`, so
 * prices come back already discounted), then the search re-runs.
 */
const applyDiscountGrant = async (dispatch, getState, discount) => {
  dispatch({ type: SET_DISCOUNT, payload: discount });

  const queryParams = {
    ...getState().route.queryParams,
    discount: discount.code,
  };
  dispatch({ type: SET_QUERY_PARAMS, payload: queryParams });

  // Re-run the search with the discountCode (getJSON accepts a queryParams
  // object directly when `.type` is set). getJSON re-enables the blur cards
  // at the end of every search, so re-hide them once it settles — the visitor
  // just unlocked.
  await dispatch(getJSON(queryParams));
  dispatch(showSearchBlurCards(false));
};

/**
 * @function
 * @name unlockRates
 * @description POST the visitor's email + captcha token (+ the live search
 * context) to pf-comms /rate-gate/unlock and drive `state.route.rateGate`.
 * On success the email is persisted (seeds the booking prefill) and the blur
 * overlay is dismissed; a returned discount grant is applied live.
 * @param {Object} input { email, captchaToken }
 * @returns {Redux.Action} thunk resolving to the rateGateApi result
 */
export const unlockRates = ({ email, captchaToken }) => async (dispatch, getState) => {
  const { rateGate } = getState().route;
  if (rateGate?.status === RateGateStatus.VERIFYING) {
    return { status: UnlockOutcome.ERROR, message: 'already verifying' };
  }

  dispatch(setRateGate({ status: RateGateStatus.VERIFYING, errorMessage: null }));

  const search = buildSearchContext({
    queryParams: getState().route.queryParams,
    fclCounters: getState().code_config.fclCounters,
    lclShipment: getState().code_config.lclShipment,
    airShipment: getState().code_config.airShipment,
  });

  const result = await rateGateApi.unlockRates({ email, captchaToken, search });

  switch (result.status) {
    case UnlockOutcome.UNLOCKED: {
      // Persist the email (redux-persist — seeds the booking contact prefill),
      // reveal the cards, remember the CRM lead for the booking payload.
      dispatch(saveSearchBlurCardsEmail(email));
      dispatch(showSearchBlurCards(false));
      dispatch(
        setRateGate({
          status: RateGateStatus.UNLOCKED,
          errorMessage: null,
          leadId: result.leadId,
          loginUrl: null,
        })
      );
      if (result.discount?.id) {
        await applyDiscountGrant(dispatch, getState, result.discount);
      }
      break;
    }
    case UnlockOutcome.GATED: {
      dispatch(
        setRateGate({
          status: RateGateStatus.GATED,
          errorMessage: null,
          leadId: null, // never let a stale unlock's lead ride into a gated session
          loginUrl: result.loginUrl || null,
        })
      );
      break;
    }
    default: {
      dispatch(
        setRateGate({
          status: RateGateStatus.ERROR,
          errorMessage: RATE_GATE_ERROR_COPY,
          leadId: null,
          loginUrl: null,
        })
      );
    }
  }

  return result;
};

/**
 * @function
 * @name requestQuote
 * @description Zero-result capture — the searched lane returned no published
 * rate, so ask Prime Freight to quote it manually. Builds the live
 * SearchContext from the same redux slices as {@link unlockRates}, so sales
 * sees exactly what was searched, then hands off to the pf-comms proxy
 * (rateGateApi.requestQuote — captures the lead + alerts sales server-side).
 * Pass-through of the result; never throws. On success we persist the email
 * (same store unlock uses) so a later unlock/booking on this session reuses it.
 * @param {{ email:string, captchaToken:string, note?:string }} input
 * @returns {Redux.Action} thunk resolving to
 *   { ok, leadId?, delivered? } | { ok:false, error }
 */
export const requestQuote = ({ email, captchaToken, note }) => async (dispatch, getState) => {
  const search = buildSearchContext({
    queryParams: getState().route.queryParams,
    fclCounters: getState().code_config.fclCounters,
    lclShipment: getState().code_config.lclShipment,
    airShipment: getState().code_config.airShipment,
  });

  const result = await rateGateApi.requestQuote({ email, captchaToken, search, note });

  if (result.ok && email) dispatch(saveSearchBlurCardsEmail(email));

  return result;
};

/**
 * @function
 * @name requestRateGateAccess
 * @description Hard-gate "Request access" — asks Prime Freight to provision a
 * portal login for this email. Resolves { ok } so the card can toast.
 * @param {string} email
 * @returns {Redux.Action} thunk resolving to { ok }
 */
export const requestRateGateAccess = (email) => async () =>
  rateGateApi.requestAccess({ email });

/**
 * @function
 * @name resetRateGateError
 * @description Return an errored gate to the idle form (retry affordance).
 * @returns {Redux.Action} thunk
 */
export const resetRateGateError = () => (dispatch) => {
  dispatch(setRateGate({ status: RateGateStatus.IDLE, errorMessage: null }));
};

/**
 * @function
 * @name submitPublicBooking
 * @description Submit the one-page "Confirm & book" form: builds the single
 * JSON contract payload (util/publicBookingPayload — pure, jest-covered) and
 * posts it to the pf-comms booking PROXY (rateGateApi.submitBooking) — the
 * browser never talks to Betty; pf-comms holds the captcha secret + action id
 * and does the CRM wiring (activities + promotion) server-side. Also persists
 * the customer form into `booking_customer_form` so the post-booking follow-ups
 * (SignUp credit application / BookViaAgent) come prefilled.
 * @param {Object} input { captchaToken, contact, company, shipper, broker,
 *   insurance?, dutyTax?, warehouse? } — the optional add-ons come from
 *   ConfirmAndBook's AdditionalServicesSection state.
 * @returns {Redux.Action} thunk resolving to
 *   { ok, bookingId, primeFreightRef, … } | { ok:false, error }
 */
export const submitPublicBooking =
  ({ captchaToken, contact, company, shipper, broker, insurance, dutyTax, warehouse }) =>
  async (dispatch, getState) => {
    const state = getState();
    const role =
      contact?.role ||
      state.code_config.transactionType ||
      'Importer';

    // Prefill source for the post-booking SignUp / BookViaAgent flows (they
    // read state.route.booking_customer_form, same as the legacy tab flow).
    dispatch(
      saveBookingFormInfo({
        customer_type: role,
        customer_first_name: contact.firstName,
        customer_last_name: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        company_name: company.companyName,
        street: company.street,
        address: company.address2,
        city: company.city,
        state: company.state,
        postal: company.postal,
        country: company.country,
      })
    );

    const payload = buildPublicBookingPayload({
      captchaToken,
      contact,
      company,
      shipper,
      broker,
      finalSearchForm: state.route.final_search_form,
      queryParams: state.route.queryParams,
      agentInformation: state.form.AgentInformation,
      consigneeRole: role,
      discount: state.route.discount,
      leadId: state.route.rateGate ? state.route.rateGate.leadId : null,
      // Optional add-ons collected on the confirmation (ConfirmAndBook state):
      // insurance = booked-policy commodities; dutyTax = {duties,taxes} subtotal;
      // warehouse = lightweight request flag surfaced to sales (no public RFQ).
      insurance: Array.isArray(insurance) ? insurance : [],
      dutyTax: dutyTax || undefined,
      warehouse: warehouse || undefined,
      fclCounters: state.code_config.fclCounters,
      lclShipment: state.code_config.lclShipment,
      airShipment: state.code_config.airShipment,
    });

    // One call: pf-comms proxies to the Betty action (holding the captcha
    // secret + action id server-side) AND does the CRM wiring (BOOKING/QUOTE
    // activities, lead promotion) in the same request.
    const result = await rateGateApi.submitBooking(payload);
    // activity-crm PR-2: log the conversion to the BB event stream (D8).
    // Fire-and-forget — never gate the booking result on telemetry.
    if (result?.ok) {
      rateGateApi.reportBookingCreated({
        leadId: state.route.rateGate?.leadId ?? null,
        bookingRef: result.bookingId,
      });
    }
    return result;
  };
